import type * as Types from '../../types'

import {
	NetworkFlags,
	BasicNetworkFlags,
	ResourceNetworkFlags,
	BasicStateWithNetwork,
	NetworkStatus,
	UpdateableResourceMeta
} from './Network.types'

/******************************
 * Private
 * - Consts
 *****************************/

/**
 * Maximum amount of flags to check in {@link getNetworkFlags} function. This is a minor optimization
 * in case many resources are loaded in one page. This will stop the loop from running
 * once all flags in {@link NetworkFlags} are set to true.
 */
const MAX_FLAGS = Object.keys(asNetworkFlags()).length

/******************************
 * Public
 * - Functions
 *****************************/

/**
 * Creates a {@link NetworkFlags} object.
 */
export function asNetworkFlags(): NetworkFlags {
	return {
		isInitialized: false,
		isLoading: false,
		isPending: false,
		hasError: false,
		hasEmpty: true,
		allEmpty: true,
		isReady: false
	}
}

/**
 * Creates a {@link BasicNetworkFlags} object.
 */
export function asBasicNetworkFlags(
	isLoading = false,
	isUpdating = false,
	hasFetched = false
): BasicNetworkFlags {
	return {
		isLoading,
		isUpdating,
		hasFetched,
	}
}

/**
 * Creates a {@link ResourceNetworkFlags} object.
 */
export function asResourceNetworkFlags(
	isLoading = false,
	isUpdating = false,
	hasFetched = false,
	hasError = true,
	isEmpty = true
): ResourceNetworkFlags {
	return {
		isLoading,
		isUpdating,
		hasFetched,
		hasError,
		isEmpty
	}
}

/**
 * Creates a {@link ResourceNetworkFlags} object.
 */
export function asUpdateableResourceMeta(
	lastUpdate: Types.Maybe<Date> = false,
	updateCount = 0
): UpdateableResourceMeta {
	return {
		lastUpdate,
		updateCount
	}
}

export function calculateNetworkFlags<S extends BasicStateWithNetwork>(
	state: S,
	getCount: (state: S) => number
): ResourceNetworkFlags {
	return {
		...state.flags,
		isEmpty: getCount(state) === 0,
		hasFetched: state.meta.lastUpdate !== false,
		hasError: state.error !== false
	}
} 

/**
 * Extracts a {@link NetworkStatus} from a {@link ResourceNetworkFlags} object.
 * 
 * @param list - list of resources to test length of
 * @param flags - flags to extract status from
 */
export function calculateNetworkStatus(
	list: unknown[],
	flags: ResourceNetworkFlags
): NetworkStatus {
	const { 
		isLoading,
		isUpdating,
		hasError,
		hasFetched
	} = flags
	if(isLoading === true) return 'LOADING'
	else if(isUpdating === true) return 'PENDING'
	else if(hasError === true) return 'ERROR'
	else if(hasFetched === false) return 'INITIAL'
	else if(list.length) return 'READY'
	else return 'EMPTY'
}


/**
 * Creates a {@link NetworkFlags} object by looping over a list of {@link NetworkStatus}
 * and interpreting each status occurance as a boolean.
 * 
 * @param flags 
 */
export function getNetworkFlags(...flags: NetworkStatus[]): NetworkFlags {
	const networkFlags = asNetworkFlags()
	networkFlags.isInitialized = true
	let flagsSet = 0
	let flag: Types.Optional<NetworkStatus>
	while(flags.length && flagsSet < MAX_FLAGS){
		flag = flags.shift()
		if(!flag) break
		if(flag === 'INITIAL'){
			networkFlags.isInitialized = false
			networkFlags.allEmpty = false
			flagsSet++
		} else if(flag === 'LOADING'){
			networkFlags.isLoading = true
			networkFlags.allEmpty = false
			flagsSet++
		} else if(flag === 'PENDING'){
			networkFlags.isPending = true
			networkFlags.allEmpty = false
			flagsSet++
		} else if(flag === 'ERROR'){
			networkFlags.hasError = true
			flagsSet++
		} else if(flag === 'READY'){
			networkFlags.isReady = true
			networkFlags.allEmpty = false
			flagsSet++
		} else if(flag === 'EMPTY'){
			networkFlags.hasEmpty = true
			flagsSet++
		}
	}

	return networkFlags
}